<script setup>
  import BbModal from 'common/components/BbModal';
  import NewCarousel from '../../NewCarousel';
  import useCatalogFilters from 'composables/useCatalogFilters';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { computed } from 'vue';
  import useIsMobile from 'common/composables/useIsMobile';

  const route = useRoute();
  const router = useRouter();

  const { isMobile } = useIsMobile();

  const props = defineProps({
    isRetailMode: {
      type: Boolean,
      default: false,
    },
  });

  const {
    getActivityPreview,
    onActivityBySlug,
    onToggleModalReservation,
    onToggleModalPreview,
  } = useCatalogFilters();

  const isGiftCatalog = computed(() => {
    return Boolean(route.name === 'box-catalog' && route.params.hash);
  });

  const isMultipleLocation = computed(() => {
    return getActivityPreview.value?.partneractivity?.locations?.length > 1;
  });

  const experienceLocation = computed(() => {
    if (
      getActivityPreview?.value?.partneractivity?.locations[0]
        .delivery_in_all_country
    ) {
      return 'Envío a todo el país';
    } else if (getActivityPreview.value.activity_type === 'online') {
      return 'Formato online';
    }
    return `${getActivityPreview?.value?.partneractivity?.locations[0]?.sublocation} - ${getActivityPreview?.value?.partneractivity?.locations[0].address}`;
  });
  const activityClicked = () => {
    const newRoute = isGiftCatalog.value
      ? {
          name: 'box-activity-catalog',
          params: {
            categorySlug: getActivityPreview.value.category.slug,
            boxSlug: route.params.boxSlug,
            activitySlug: getActivityPreview.value.slug,
            hash: route.params.hash,
          },
        }
      : {
          name: 'boxActivity',
          params: {
            categorySlug: getActivityPreview.value.category.slug,
            boxSlug: route.params.boxSlug,
            activitySlug: getActivityPreview.value.slug,
          },
        };

    if (props.isRetailMode) {
      newRoute.query = {
        ...newRoute.query,
        retail: 'true',
      };
    }

    let routeUrl = router.resolve(newRoute).href;
    window.open(routeUrl, '_blank');
  };

  const openReservationModal = async () => {
    await onActivityBySlug();
    onToggleModalPreview();
    onToggleModalReservation();
  };

  const participants = computed(() => {
    const count = Number(getActivityPreview.value.participants);

    if (count === 1) {
      return { key: 'individual', value: 1 };
    } else if (count === 2) {
      return { key: 'duo', value: 2 };
    } else if (count > 2) {
      return { key: 'multiple', value: count };
    }
  });

  const participantsIconMap = {
    individual: require('common/icons/1Parcitipante.svg'),
    duo: require('common/icons/Users.icon.svg'),
    multiple: require('common/icons/Users.icon.svg'), //fallback icon
  };

  const participantsIcon = computed(() => {
    if (!participants.value) return '';
    const iconSrc = participantsIconMap[participants.value.key];
    return typeof iconSrc === 'object' && iconSrc.default
      ? iconSrc.default
      : iconSrc;
  });
</script>

<template>
  <bb-modal
    :smallModalBox="true"
    :customPadding="{
      desktop: '0 24px 0 24px',
      mobile: '0 12px 0 12px',
    }"
    @close="() => $emit('close')"
  >
    <div class="modalHeader">
      <h3 class="title">
        {{ getActivityPreview.name }}
      </h3>
    </div>
    <div class="activityPreviewModal bb-scrollbar-new">
      <new-carousel
        :images="getActivityPreview.images"
        borderColor="white"
        :isExperiencePreview="true"
        class="activityCarousel"
        style="height: 300px"
        :showDots="true"
        :showThumbnails="!isMobile"
      />

      <h3 class="subtitle bb-my-4">¿Qué incluye la experiencia?</h3>

      <div class="content">
        <section>
          <div class="item">
            <div
              :class="{
                'icon-person-multiple':
                  participants?.key === 'multiple' ||
                  participants?.key === 'duo',
                'icon-person': participants?.key === 'individual',
              }"
              v-html="participantsIcon"
            ></div>

            <span>
              Para
              {{ plural(participants?.value, 'persona', true) }}
            </span>
          </div>
          <span> {{ getActivityPreview.benefits_title }} </span>
          <div
            class="bb-dynamic-content"
            v-html="getActivityPreview.benefits"
          ></div>
        </section>

        <section v-if="getActivityPreview.small_print">
          <div class="item item-highlight">
            <div
              class="icon-calendar"
              v-html="require('common/icons/Calendar_Days.svg')"
            ></div>
            <span>Reserva</span>
          </div>
          <div class="bb-column">
            <span>{{ getActivityPreview.small_print }}</span>
          </div>
        </section>

        <section>
          <div class="item item-highlight">
            <div
              class="icon-location"
              v-html="require('common/icons/sellspot.svg')"
            ></div>
            <h3>Dirección</h3>
          </div>
          <span class="address" v-if="isMultipleLocation"
            >Múltiples localidades</span
          >
          <span class="address" v-else>{{ experienceLocation }}</span>
        </section>

        <section v-if="getActivityPreview.know_what">
          <div class="item item-highlight">
            <div
              class="icon-know-what"
              v-html="require('common/icons/SabiasQue.svg')"
            ></div>
            <p>Más info</p>
          </div>
          <span
            class="bb-dynamic-content"
            v-html="getActivityPreview.know_what"
          />
        </section>

        <section v-if="getActivityPreview.tip">
          <div class="item item-highlight">
            <div
              class="icon-tips"
              v-html="require('common/icons/Lamp.svg')"
            ></div>
            <p>Bigtips</p>
          </div>
          <p>{{ getActivityPreview.tip }}</p>
        </section>
      </div>
    </div>
    <div class="modalFooter bb-flex bb-justify-content-center bb-row-to-column">
      <button
        class="bb-button --new-primary-secondary"
        @click="activityClicked"
      >
        Ver en otra ventana
      </button>
      <button
        v-if="isGiftCatalog"
        class="bb-button --black"
        @click="openReservationModal"
      >
        Reservar
      </button>
    </div>
  </bb-modal>
</template>

<style scoped lang="scss">
  .modalHeader {
    position: sticky;
    top: 0;
    width: 100%;
    height: 64px;
    display: flex;
    align-items: center;
    background-color: white;
    .title {
      font-weight: 600;
      font-size: 20px;
      line-height: 26px;
    }
  }

  .activityPreviewModal {
    position: relative;
    height: calc(75vh - 100px);
    overflow-y: auto;
    .content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .subtitle {
      font-weight: 600;
      font-size: 18px;
    }
    .item {
      color: $DARK-GRAY;
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
      width: fit-content;
      gap: 8px;
      padding: 2px 4px;
      border-radius: 4px;
      display: flex;
      flex-direction: row;
      align-items: center;

      @media (max-width: $screen-xs-max) {
        font-size: 14px;
      }
      &-highlight {
        background-color: $GRAY-6;
      }
    }
    .icon {
      &-calendar {
        width: 22px;
        height: 20px;
      }
      &-person {
        width: 12px;
        height: 14px;
      }
      &-person-multiple {
        width: 20px;
        height: 20px;
      }
      &-know-what {
        width: 20px;
        height: 14px;
      }
      &-location {
        width: 20px;
        height: 15px;
        align-self: start;
      }
      &-tips {
        width: 20px;
        height: 20px;
      }
    }
  }

  .modalFooter {
    position: sticky;
    bottom: 0;
    width: 100%;
    height: fit-content;
    padding: 18px 0 18px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    button {
      border-radius: 8px;
      width: 296px;
      font-size: 16px;
      font-weight: 600;
    }
  }
</style>
